import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Reference } from '@1po/1po-bff-fe-spec/generated/basket/response/model/OtherSection';
import { AngleDownIcon, AngleRightIcon, TrashAltIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import { removeReferencesRequest } from 'domains/basket/Basket.store';
import { ExternalBasketSectionLocal, VehicleLocal } from 'domains/basket/Basket.types';
import { collectAllReferences } from 'domains/order/Order.mapper';
import { refWithoutDepositRef } from 'domains/references/DepositReference';
import { SectionWrapper } from 'pages/CartPage/CartPopover/CartPopover.styled';
import { ReferenceCard } from 'pages/CartPage/CartPopover/ReferenceCard';
import { renderVehicleInfoBlock } from 'pages/CartPage/ValidationStep/VerificationCard/VerificationCard';
import { SectionPicBackground } from 'pages/CartPage/ValidationStep/VerificationCard/VerificationCard.styled';
import { SectionImage } from 'pages/CartPage/ValidationStep/VerificationCard/VerificationImage';
import { theme } from 'styles';
import { Flex, Icon, Text } from 'UI';

interface SectionCardProps {
  vehicle?: VehicleLocal;
  externalBasket?: ExternalBasketSectionLocal;
  references?: Reference[];
  'data-cy'?: string;
}

const SectionCard = ({ vehicle, externalBasket, references, 'data-cy': dataCy }: SectionCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deleteDisclosure = useDisclosure();
  const refArray = (vehicle ? vehicle?.references : references) || [];
  const externalBasketId = externalBasket?.externalBasketId;
  const referencesCount = refWithoutDepositRef(refArray)?.length;
  const vehicleInfoBlock = renderVehicleInfoBlock(vehicle, externalBasket, t);
  const vehicleImageUrl = vehicle?.vehicleDetail?.imageUrl;
  const [isSectionCollapsed, setIsSectionCollapsed] = useState<boolean>(false);

  const deleteConfirmationText = vehicle
    ? t('cart.action.removalText2', 'This action will delete your vehicle basket and all attached references.')
    : t('cart.action.removalText4', 'This action will delete all references in this section.');

  const handleDeleteSection = () => {
    if (vehicle) {
      dispatch(
        removeReferencesRequest({
          vehiclesWithReferences: [
            {
              vehicleKey: vehicle.vehicleDetail.vehicleKey,
              references: collectAllReferences(vehicle.references),
            },
          ],
        }),
      );
    } else if (externalBasket) {
      dispatch(
        removeReferencesRequest({
          externalBasketsWithReferences: [
            {
              externalBasketId: externalBasket.externalBasketId,
              references: externalBasket.references
                .map((reference) => reference.referenceNumber)
                .filter((referenceNumber): referenceNumber is string => Boolean(referenceNumber)),
            },
          ],
        }),
      );
    } else {
      dispatch(
        removeReferencesRequest({
          otherReferences: collectAllReferences(refArray),
        }),
      );
    }
  };

  return (
    <SectionWrapper direction={'column'} data-cy={dataCy}>
      <Flex direction={'row'} align={'center'} padding={10} gap={10}>
        <SectionPicBackground showBackground={!vehicle} height={70} width={70} isSmall>
          <SectionImage
            vehicle={vehicle}
            imageUrl={vehicleImageUrl}
            externalBasketId={externalBasketId}
            maxWidth={70}
            maxHeight={70}
            data-cy={'cart-preview-vehicle-image'}
          />
        </SectionPicBackground>
        <Flex direction={'column'} justify={'center'} minHeight={70} data-cy={'cart-preview-section-name'}>
          {vehicleInfoBlock}
        </Flex>
        <Flex direction={'row-reverse'} maxWidth={135}>
          <Text type="lg" disableGutter data-cy={'cart-preview-section-count'}>
            {t('cart.preview.section.reference_count', '{{count}} References', { count: referencesCount })}
          </Text>
        </Flex>
        <Flex direction={'row-reverse'} maxWidth={35} align={'center'}>
          <Icon
            IconComponent={TrashAltIcon}
            round
            size={20}
            color={theme.color.grey15}
            onClick={deleteDisclosure.onOpen}
            data-cy={'cart-preview-section-delete'}
          />
        </Flex>
        <Flex direction={'row-reverse'} maxWidth={35} align={'center'} data-cy={'cart-preview-angle-right-icon'}>
          <Icon
            IconComponent={isSectionCollapsed ? AngleRightIcon : AngleDownIcon}
            round
            size={35}
            color={theme.color.grey15}
            onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
            data-cy={'cart-preview-angle-down-icon'}
          />
        </Flex>
      </Flex>
      {!isSectionCollapsed &&
        refArray.map((reference) => (
          <ReferenceCard
            key={`refPrevCard-${reference.referenceNumber}${reference.sellerNumber ?? ''}`}
            reference={reference}
            vehicle={vehicle}
          />
        ))}
      <Dialog
        disclosure={deleteDisclosure}
        title={t('common.dialog.actionRequired', 'Action required')}
        description={[deleteConfirmationText, t('common.dialog.confirmationQuestion', 'Would you like to confirm?')]}
        icon={TrashAltIcon}
        status={'info'}
        handleConfirm={handleDeleteSection}
      />
    </SectionWrapper>
  );
};

export default SectionCard;
